import React from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Table from '@lib/components/Table/Table';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import Search from '@lib/components/Search/Search';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import useSearchParam from '@lib/hooks/useSearchParam';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import Trans from '@lib/components/Trans/Trans';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import { IconName } from '@lib/components/Modal/enums';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import { ActionsResult } from 'routes/Sectors/SectorList/utils/useActions';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import { Sector } from 'graphql-common';
import getTableActionsMenu from './utils/getTableActionsMenu';
import getTableColumns from './utils/getTableColumns';

interface Props {
  useActionsResult: ActionsResult;
  useModalManagementResult: UseModalManagementResult;
  useBulkSelectResult: UseBulkSelectResult<Sector>;
  isViewOnly: boolean;
}

function SectorList(props: Props) {
  const {
    useActionsResult,
    useBulkSelectResult,
    useModalManagementResult,
    isViewOnly,
  } = props;
  const { t } = useTranslation();
  const [searchQuery] = useSearchParam();

  const selectedSector = useBulkSelectResult.selectedSingleTableItem;

  const {
    destroyLoading,
    onAddHandler,
    onConfirmedDelete,
    onDeleteHandler,
    onViewHandler,
    onEditHandler,
    sectorsQuery,
  } = useActionsResult;

  const tableColumns = getTableColumns({
    onViewHandler,
    t,
  });
  const tableActionsMenu = getTableActionsMenu({
    onEditHandler,
    onViewHandler,
    onDeleteHandler,
    isViewOnly,
    t,
  });

  if (sectorsQuery.error) {
    return (
      <ErrorPage
        status={getStatusFromError(sectorsQuery.error)}
        description={getDescriptionFromError(sectorsQuery.error)}
      />
    );
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-sectors-yet')}
      text={t('no-sectors-text')}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="add"
                buttonText={t('create-new-sector')}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="add"
                onClick={onAddHandler}
              />,
            ]
      }
    />
  );

  return (
    <>
      <PageHeader title={t('sectors')}>
        {isViewOnly ? (
          <ViewOnlyChip />
        ) : (
          <Button
            buttonText={t('create-new-sector')}
            data-test-id="sectors__add-button"
            leftIcon="add"
            onClick={onAddHandler}
          />
        )}
      </PageHeader>
      <PageBody>
        {sectorsQuery.firstLoading ? (
          <CircleLoader />
        ) : (
          <>
            {(!!sectorsQuery.collection?.length || searchQuery) && (
              <Search
                placeholder={t('sectors-search-placeholder')}
                data-test-id="sectors__search-input"
              />
            )}
            <Table
              actionsMenu={tableActionsMenu}
              columns={tableColumns}
              counterText={t('sector-plural', {
                count: sectorsQuery.totalCount,
              })}
              data={sectorsQuery.collection}
              emptyState={emptyState}
              // fullHeight
              id="sectors"
              loading={sectorsQuery.loading}
              totalEntries={sectorsQuery.totalCount}
            />
          </>
        )}
        <ConfirmationModal
          isOpen={useModalManagementResult.isModalOpened(Modals.DestroyModal)}
          loading={destroyLoading}
          onClose={() =>
            useModalManagementResult.closeModal(Modals.DestroyModal)
          }
          onConfirm={onConfirmedDelete}
          useProgress
          title={t('are-you-sure')}
          description={
            <Trans
              i18nKey="delete-sector-text"
              values={{ name: selectedSector?.name }}
            />
          }
          confirmButtonLabel={t('yes-delete')}
          icon={IconName.Delete}
          cancelButtonLabel={t('cancel')}
        />
      </PageBody>
    </>
  );
}

export default SectorList;
